import clsx from 'clsx';
import {
  type FormEvent,
  forwardRef,
  useState,
  Children,
  useEffect,
} from 'react';

import { ButtonGroupContext } from './contexts/ButtonGroupContext.js';
import * as styles from './ToggleButtonGroup.sty.js';
import type {
  RadioGroupState,
  ToggleButtonGroupItemProps,
  ToggleButtonGroupProps,
} from './types.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';

//#region Typings

interface ToggleButtonGroupElementProps {
  /** The props of an item in the ToggleButtonGroup */
  props: ToggleButtonGroupItemProps;
}
//#endregion

/**
 * The `ToggleButtonGroup` is used to group related options.
 * The group consists of at least two `ToggleButtonGroupItems`.
 * The `ToggleButtonGroup` supports exclusive selection only.
 * That means that selecting one option deselects any other.
 * @public
 */
export const ToggleButtonGroup = forwardRef<
  HTMLDivElement,
  ToggleButtonGroupProps
>((props, forwardedRef) => {
  const {
    children,
    disabled: isDisabled = false,
    required: isRequired = false,
    readOnly: isReadOnly = false,
    value,
    defaultValue,
    name,
    onChange,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: ToggleButtonGroupProps = props;

  const radioValue =
    value !== undefined && value.length > 0 ? value : undefined;

  const [selectedValue, setSelectedValue] = useControllableState({
    defaultValue,
    value: radioValue,
  });

  const [selectedIsDisabled, setSelectedIsDisabled] = useState(false);
  const [firstFocusableValue, setFirstFocusableValue] = useState<string | null>(
    null,
  );

  const radioFocusDefaultValue =
    defaultValue !== undefined ? defaultValue : firstFocusableValue;

  const [lastFocusedValue, setLastFocusedValue] = useControllableState({
    defaultValue: radioFocusDefaultValue,
    value: radioValue,
  });

  const mappedChildren = Children.map(children, (child) => child);

  useEffect(() => {
    let firstFocusable: string | null = null;

    mappedChildren?.forEach((child) => {
      const item = (child as ToggleButtonGroupElementProps).props;

      // Find the first item that should be focused if no item is selected.
      if (firstFocusable === null && !item.disabled) {
        firstFocusable = item.value;
        if (firstFocusable !== firstFocusableValue) {
          setFirstFocusableValue(firstFocusable);
        }
      }
      // Check if the selected item is disabled.
      if (selectedValue === item.value) {
        if (item.disabled) {
          setSelectedIsDisabled(true);
        } else if (selectedIsDisabled) {
          setSelectedIsDisabled(false);
        }
      }
    });
  }, [firstFocusableValue, mappedChildren, selectedIsDisabled, selectedValue]);

  const state: RadioGroupState = {
    id: name,
    isDisabled,
    isReadOnly,
    isRequired,
    selectedValue,
    setSelectedValue,
    lastFocusedValue,
    setLastFocusedValue,
    firstFocusableValue,
    selectedIsDisabled,
  };

  const handleChange = (
    value: string,
    event?: FormEvent<HTMLElement | SVGElement>,
  ) => {
    if (!event) {
      return;
    }

    onChange?.(value, event);
  };

  return (
    <div
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={forwardedRef}
      role="radiogroup"
      aria-orientation="horizontal"
      className={clsx(styles.buttonGroup, consumerClassName)}
      style={consumerStyle}
      {...remainingProps}
    >
      <ButtonGroupContext.Provider
        value={{ state: { ...state, onChange: handleChange } }}
      >
        {children}
      </ButtonGroupContext.Provider>
    </div>
  );
});
